import {
  Drawer,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Theme,
  Tooltip,
  useTheme
} from '@mui/material';
import React from 'react';
import { ReactComponent as CreateIcon } from '../../../assets/icons/Create.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import {
  CarbonTarget,
  CarbonTargetDetails,
  ReportingPeriod,
  ReportingPeriodGroup
} from '@esg/esg-global-types';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { DataGrid, GridActionsCellItem, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import ConfigAddWidget, { CreateInput } from '../ConfigAddWidget';
import ConfigEditWidget, { EditInput } from '../ConfigEditWidget';
import { DocumentReference } from 'firebase/firestore';
import { auth } from '../../../lib/google/firebase';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import {
  CarbonTargetData,
  carbon_target_label,
  createCarbonTarget,
  deleteCarbonTarget,
  getCarbonTargets,
  updateCarbonTarget
} from '../../../lib/app/carbon_target';
import ReportingPeriodGroupSelect from '../../shared/input/select/ReportingPeriodGroupSelect';
import moment, { Moment } from 'moment';
import {
  ReportingPeriodGroupExtended,
  getReportingPeriodGroups,
  joinReportingPeriodGroupsReportingPeriods
} from '../../../lib/metric_capture/reporting_period_group';
import { getReportingPeriods } from '../../../lib/metric_capture/reporting_period';

interface CarbonTargetGridRow {
  id: string;
  created: Date | Moment;
  modified: Date | Moment | null;
  deleted: Date | Moment | null;
  type: 'percentage' | 'emissions';
  breakdown: 'total' | 'by_scope';
  baseline_year: ReportingPeriodGroup | ReportingPeriodGroupExtended;
  target_year: ReportingPeriodGroup | ReportingPeriodGroupExtended;
  target_total: number | undefined;
  target_scope_1: number | undefined;
  target_scope_2: number | undefined;
  target_scope_3: number | undefined;
}

/**
 * Datagrid handling the display of configured Carbon Emission Targets.
 * @returns {JSX.Element}
 */
const PanelCarbonTargets = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [carbonTargetRows, setCarbonTargetRows] = React.useState<Array<CarbonTargetGridRow>>([]);
  const [reportingPeriodGroups, setReportingPeriodGroups] = React.useState<
    Array<ReportingPeriodGroupExtended>
  >([]);
  const [actionRow, setActionRow] = React.useState<CarbonTargetGridRow | null>(null);
  const [isCreateWidget, setIsCreateWidget] = React.useState<boolean>(true);
  const [displayWidgetPanelRight, setDisplayWidgetPanelRight] = React.useState<boolean>(false);
  const [openDialogDelete, setOpenDialogDelete] = React.useState<boolean>(false);
  const [gridLoading, setGridLoading] = React.useState<boolean>(false);
  const [createdCarbonTarget, setCreatedCarbonTarget] = React.useState<
    Partial<CarbonTargetGridRow>
  >({});
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  const targetTypeInputFactory = (
    handleInputChange: (input_id: string, value: string) => void
  ): JSX.Element => {
    return (
      <FormControl>
        <FormLabel>Target Type</FormLabel>
        <RadioGroup
          row
          defaultValue={actionRow ? actionRow.type : 'type'}
          value={actionRow ? actionRow.type : (createdCarbonTarget.type ?? '')}
          onChange={(event, value: string) => {
            handleInputChange('type', value);
            !actionRow &&
              setCreatedCarbonTarget({
                ...createdCarbonTarget,
                type: value as 'percentage' | 'emissions'
              });
          }}
        >
          <FormControlLabel value="percentage" control={<Radio />} label="Percentage vs Baseline" />
          <FormControlLabel value="emissions" control={<Radio />} label="Target Emissions" />
        </RadioGroup>
      </FormControl>
    );
  };

  const targetBreakdownInputFactory = (
    handleInputChange: (input_id: string, value: string) => void
  ): JSX.Element => {
    return (
      <FormControl>
        <FormLabel>Target Details Breakdown</FormLabel>
        <RadioGroup
          row
          defaultValue={actionRow ? actionRow.breakdown : 'breakdown'}
          value={actionRow ? actionRow.breakdown : (createdCarbonTarget.breakdown ?? '')}
          onChange={(event, value: string) => {
            handleInputChange('breakdown', value);
            !actionRow &&
              setCreatedCarbonTarget({
                ...createdCarbonTarget,
                breakdown: value as 'total' | 'by_scope'
              });
          }}
        >
          <FormControlLabel value="total" control={<Radio />} label="Total Emissions" />
          <FormControlLabel value="by_scope" control={<Radio />} label="Emissions by Scope" />
        </RadioGroup>
      </FormControl>
    );
  };

  const baselineYearInputFactory = (
    handleInputChange: (input_id: string, value: ReportingPeriodGroup | null) => void
  ): JSX.Element => {
    return (
      <>
        {group && company && (
          <ReportingPeriodGroupSelect
            group_id={group.id}
            company_id={company.id}
            input_label="Baseline Year"
            selected_reporting_period_group={
              actionRow ? (actionRow.baseline_year as ReportingPeriodGroup) : undefined
            }
            handleChangeReportingPeriodGroups={(
              reporting_period_group: Array<ReportingPeriodGroup> | ReportingPeriodGroup | null
            ) => {
              if (!Array.isArray(reporting_period_group)) {
                handleInputChange('baseline_year', reporting_period_group);
                isCreateWidget &&
                  setCreatedCarbonTarget({
                    ...createdCarbonTarget,
                    baseline_year: reporting_period_group ? reporting_period_group : undefined
                  });
              }
            }}
          />
        )}
      </>
    );
  };

  const targetYearInputFactory = (
    handleInputChange: (input_id: string, value: ReportingPeriodGroup | null) => void
  ): JSX.Element => {
    return (
      <>
        {group && company && (
          <ReportingPeriodGroupSelect
            group_id={group.id}
            company_id={company.id}
            input_label="Target Year"
            selected_reporting_period_group={
              actionRow ? (actionRow.target_year as ReportingPeriodGroup) : undefined
            }
            helper_text={
              'NOTE: Target year start date must be after the end date of baseline year and after all existing carbon target years.'
            }
            handleChangeReportingPeriodGroups={(
              reporting_period_group: Array<ReportingPeriodGroup> | ReportingPeriodGroup | null
            ) => {
              if (!Array.isArray(reporting_period_group)) {
                handleInputChange('target_year', reporting_period_group);
              }
            }}
          />
        )}
      </>
    );
  };

  // Handler functions
  const handleCreateClick = (): void => {
    setActionRow(null);
    setCreatedCarbonTarget({});
    setIsCreateWidget(true);
    setDisplayWidgetPanelRight(true);
  };

  const handleEditClick = (row: CarbonTargetGridRow): void => {
    setCreatedCarbonTarget({});
    setActionRow(row);
    setIsCreateWidget(false);
    setDisplayWidgetPanelRight(true);
  };

  const handleCloseDeleteModal = (): void => {
    setOpenDialogDelete(false);
  };

  const handleDeleteClick = (row: CarbonTargetGridRow): void => {
    try {
      setActionRow(row);
      setOpenDialogDelete(true);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelCarbonTargets failed on an unknown error while calling handleDeleteClick.',
          {
            row: row
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete carbon target. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const handleUpdateRows = (rows_data: Array<CarbonTargetGridRow>): void => {
    setCarbonTargetRows(rows_data);
  };

  /**
   * Validate the input values for a created or updated carbon target.
   * @param {CarbonTargetData} carbon_target_data The carbon target input data to validate.
   * @param {boolean} validate_target_year_latest Control whether to compare the target year to other carbon target, for when the target year is changing.
   * @param {string} update_row_id Optionally provide the carbon target id for validation logic (when updating).
   * @returns {boolean} The valid status of the inputs.
   */
  const validateCarbonTargetRowInput = (
    carbon_target_data: CarbonTargetData,
    validate_target_year_latest: boolean,
    update_row_id?: string
  ): boolean => {
    const carbon_targets: Array<number | undefined> = [
      carbon_target_data.target.total,
      carbon_target_data.target.scope_1,
      carbon_target_data.target.scope_2,
      carbon_target_data.target.scope_3
    ];

    // Extend baseline and target input years with relevant reporting periods.
    const baseline_year_with_reporting_periods: ReportingPeriodGroupExtended | undefined =
      reportingPeriodGroups.find((reporting_period_group: ReportingPeriodGroupExtended) => {
        return reporting_period_group.id === carbon_target_data.baseline_year.id;
      });
    const target_year_with_reporting_periods: ReportingPeriodGroupExtended | undefined =
      reportingPeriodGroups.find((reporting_period_group: ReportingPeriodGroupExtended) => {
        return reporting_period_group.id === carbon_target_data.target_year.id;
      });

    // Target year cannot start before the end of baseline year.
    const valid_years: boolean =
      !!baseline_year_with_reporting_periods &&
      !!target_year_with_reporting_periods &&
      moment
        .min(
          target_year_with_reporting_periods.reporting_periods.map(
            (reporting_period: ReportingPeriod) => moment(reporting_period.start)
          )
        )
        .isAfter(
          moment.max(
            baseline_year_with_reporting_periods.reporting_periods.map(
              (reporting_period: ReportingPeriod) => moment(reporting_period.start)
            )
          )
        );
    if (!valid_years) {
      setFeedbackData({
        message: 'Target year must have a start date after the end date of baseline year',
        state: true,
        type: 'error'
      });
      return false;
    }

    if (validate_target_year_latest) {
      // New target year must be after latest target year of existing targets.
      let latest_target_year_end: Moment = moment('1900-01-01');
      const new_target_year_start_date: Moment | undefined = target_year_with_reporting_periods
        ? moment.min(
            target_year_with_reporting_periods.reporting_periods.map(
              (reporting_period: ReportingPeriod) => moment(reporting_period.start)
            )
          )
        : undefined;
      // Determine the latest end date of all existing carbon target reporting period groups, not including the current one being created/updated.
      const filtered_existing_carbon_targets: Array<CarbonTargetGridRow> = update_row_id
        ? carbonTargetRows.filter(
            (carbon_target: CarbonTargetGridRow) => carbon_target.id !== update_row_id
          )
        : carbonTargetRows;
      filtered_existing_carbon_targets.forEach((carbon_target: CarbonTargetGridRow) => {
        const carbon_target_year_with_reporting_periods: ReportingPeriodGroupExtended | undefined =
          reportingPeriodGroups.find((reporting_period_group: ReportingPeriodGroupExtended) => {
            return reporting_period_group.id === carbon_target.target_year.id;
          });
        if (carbon_target_year_with_reporting_periods) {
          const current_target_year_end: Moment = moment.max(
            carbon_target_year_with_reporting_periods.reporting_periods.map(
              (reporting_period: ReportingPeriod) => moment(reporting_period.start)
            )
          );
          latest_target_year_end = current_target_year_end.isAfter(latest_target_year_end)
            ? current_target_year_end
            : latest_target_year_end;
        }
      });
      if (
        new_target_year_start_date &&
        latest_target_year_end.isAfter(new_target_year_start_date)
      ) {
        setFeedbackData({
          message: `Target year must be after latest existing carbon target end date: ${latest_target_year_end.format('YYYY-MM-DD')}`,
          state: true,
          type: 'error'
        });
        return false;
      }
    }

    // Percentage emission change cannot be lower than -100%.
    if (carbon_target_data.type === 'percentage') {
      if (carbon_targets.some((value: number | undefined) => !!value && value < -100)) {
        setFeedbackData({
          message: 'Percentage targets cannot be less than -100%',
          state: true,
          type: 'error'
        });
        return false;
      }
    }

    // Actual emissions target cannot be negative tCO2e.
    if (carbon_target_data.type === 'emissions') {
      if (carbon_targets.some((value: number | undefined) => !!value && value < 0)) {
        setFeedbackData({
          message: 'Emission targets cannot be less than 0 tCO2e',
          state: true,
          type: 'error'
        });
        return false;
      }
    }
    return true;
  };

  // Fetch Reporting Period Groups with attached Reporting Periods for input validation.
  const fetchReportingPeriodGroups = async (): Promise<void> => {
    try {
      if (group && company) {
        // Load rows from database.
        const [reporting_period_groups, reporting_periods]: [
          Array<ReportingPeriodGroup>,
          Array<ReportingPeriod>
        ] = await Promise.all([
          getReportingPeriodGroups(group.id, company.id),
          getReportingPeriods(group.id, company.id)
        ]);
        const reporting_period_groups_with_reporting_periods: Array<ReportingPeriodGroupExtended> =
          joinReportingPeriodGroupsReportingPeriods(reporting_period_groups, reporting_periods);
        // Load rows to memory.
        setReportingPeriodGroups(reporting_period_groups_with_reporting_periods);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelCarbonTargets failed on an unknown error while calling fetchReportingPeriodGroups.',
          {
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to load Reporting Period Groups. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  // Row functions
  const fetchRows = async (): Promise<void> => {
    setGridLoading(true);
    try {
      // Load rows from database.
      if (group && company) {
        const carbon_targets: Array<CarbonTarget> = await getCarbonTargets(group.id, company.id);
        // Map and load rows to memory.
        if (carbon_targets.length > 0) {
          const carbon_target_rows: Array<CarbonTargetGridRow> = carbon_targets.map(
            (carbon_target: CarbonTarget) => {
              return {
                ...carbon_target,
                baseline_year: carbon_target.baseline_year as ReportingPeriodGroup,
                target_year: carbon_target.target_year as ReportingPeriodGroup,
                target_total: carbon_target.target.total,
                target_scope_1: carbon_target.target.scope_1,
                target_scope_2: carbon_target.target.scope_2,
                target_scope_3: carbon_target.target.scope_3
              };
            }
          );
          setCarbonTargetRows(carbon_target_rows);
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelCarbonTargets failed on an unknown error while calling fetchRows.',
          {},
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch carbon targets. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setGridLoading(false);
    }
  };

  const createRow = async (
    type: 'percentage' | 'emissions',
    breakdown: 'total' | 'by_scope',
    baseline_year: ReportingPeriodGroup,
    target_year: ReportingPeriodGroup,
    target_total: number | undefined,
    target_scope_1: number | undefined,
    target_scope_2: number | undefined,
    target_scope_3: number | undefined
  ): Promise<void> => {
    try {
      if (group && company) {
        const target_details: CarbonTargetDetails = {
          total: target_total,
          scope_1: target_scope_1,
          scope_2: target_scope_2,
          scope_3: target_scope_3
        };
        const carbon_target_data: CarbonTargetData = {
          created: new Date(),
          modified: null,
          deleted: null,
          type: type,
          breakdown: breakdown,
          baseline_year: baseline_year,
          target_year: target_year,
          target: target_details
        };
        if (!validateCarbonTargetRowInput(carbon_target_data, true)) {
          throw new Error('invalid create input');
        }
        const new_carbon_target: DocumentReference = await createCarbonTarget(
          group.id,
          company.id,
          carbon_target_data,
          auth.currentUser?.email ?? ''
        );
        if (new_carbon_target) {
          handleUpdateRows([
            {
              id: new_carbon_target.id,
              ...carbon_target_data,
              baseline_year: baseline_year,
              target_year: target_year,
              target_total: target_total,
              target_scope_1: target_scope_1,
              target_scope_2: target_scope_2,
              target_scope_3: target_scope_3
            },
            ...carbonTargetRows
          ]);
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'invalid create input') {
        throw err;
      }
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelCarbonTargets failed on an unknown error while calling createRow.',
          {
            group: group,
            company: company,
            type: type,
            breakdown: breakdown,
            baseline_year: baseline_year,
            target_year: target_year,
            target_total: target_total,
            target_scope_1: target_scope_1,
            target_scope_2: target_scope_2,
            target_scope_3: target_scope_3
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to create Carbon Target. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const updateRow = async (
    updated_row: CarbonTargetGridRow,
    original_data: CarbonTargetGridRow
  ): Promise<void> => {
    try {
      if (group && company) {
        const updated_carbon_target_data: CarbonTargetData = {
          created: updated_row.created,
          modified: new Date(),
          deleted: null,
          type: updated_row.type,
          breakdown: updated_row.breakdown,
          baseline_year: updated_row.baseline_year,
          target_year: updated_row.target_year,
          target: {
            total: updated_row.target_total,
            scope_1: updated_row.target_scope_1,
            scope_2: updated_row.target_scope_2,
            scope_3: updated_row.target_scope_3
          }
        };
        const target_year_changed: boolean =
          updated_row.target_year.id !== original_data.target_year.id;
        if (
          !validateCarbonTargetRowInput(
            updated_carbon_target_data,
            target_year_changed,
            updated_row.id
          )
        ) {
          throw new Error('invalid edit input');
        }
        const original_carbon_target: CarbonTargetData = {
          ...original_data,
          target: {
            total: original_data.target_total,
            scope_1: original_data.target_scope_1,
            scope_2: original_data.target_scope_2,
            scope_3: original_data.target_scope_3
          }
        };
        await updateCarbonTarget(
          group.id,
          company.id,
          updated_row.id,
          updated_carbon_target_data,
          original_carbon_target,
          auth.currentUser?.email ?? ''
        ).then(() => {
          handleUpdateRows(
            carbonTargetRows.map((row: CarbonTargetGridRow) =>
              updated_row.id === row.id ? { ...updated_row } : row
            )
          );
        });
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'invalid edit input') {
        throw err;
      }
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelCarbonTargets failed on an unknown error while calling updateRow.',
          {
            updated_row: updated_row,
            original_data: original_data
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update Carbon Target. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const deleteRow = async (carbon_target: CarbonTargetGridRow): Promise<void> => {
    try {
      if (group && company) {
        await deleteCarbonTarget(
          group.id,
          company.id,
          carbon_target.id,
          auth.currentUser?.email ?? ''
        ).then(() => {
          setCarbonTargetRows(
            carbonTargetRows.filter((row: CarbonTargetGridRow) => {
              return row.id !== carbon_target.id;
            })
          );
        });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelCarbonTargets failed on an unknown error while calling deleteRow.',
          {
            carbon_target: carbon_target
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to remove Carbon Target. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setOpenDialogDelete(false);
    }
  };

  React.useEffect(() => {
    try {
      (async () => {
        setCarbonTargetRows([]);
        await fetchRows();
        await fetchReportingPeriodGroups();
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelCarbonTargets failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
    return;
  }, [company]);

  const columns: Array<GridColDef> = [
    {
      field: 'created',
      headerName: 'Created',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        return params.row.created ? moment(params.row.created).format('YYYY-MM-DD HH:mm') : '';
      },
      flex: 1
    },
    {
      field: 'baseline_year',
      headerName: 'Baseline Year',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.baseline_year.name;
      },
      flex: 1
    },
    {
      field: 'target_year',
      headerName: 'Target Year',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.target_year.name;
      },
      flex: 1
    },
    {
      field: 'target_total',
      headerName: 'Total',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        return params.value
          ? `${params.value}${params.row.type === 'emissions' ? ' tCO2e' : '%'}`
          : '-';
      },
      flex: 1
    },
    {
      field: 'target_scope_1',
      headerName: 'Scope 1',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        return params.value
          ? `${params.value}${params.row.type === 'emissions' ? ' tCO2e' : '%'}`
          : '-';
      },
      flex: 1
    },
    {
      field: 'target_scope_2',
      headerName: 'Scope 2',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        return params.value
          ? `${params.value}${params.row.type === 'emissions' ? ' tCO2e' : '%'}`
          : '-';
      },
      flex: 1
    },
    {
      field: 'target_scope_3',
      headerName: 'Scope 3',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        return params.value
          ? `${params.value}${params.row.type === 'emissions' ? ' tCO2e' : '%'}`
          : '-';
      },
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            <GridActionsCellItem
              key={1}
              icon={
                <Tooltip title="Edit Carbon Target">
                  <EditIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Edit"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleEditClick(row)}
            />
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete Carbon Target">
                  <DeleteIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
            />
          </>
        ];
      }
    }
  ];

  const create_inputs: readonly CreateInput[] = [
    {
      id: 'type',
      type: 'node',
      label: 'Target Type',
      inputNodeFactory: targetTypeInputFactory
    },
    {
      id: 'breakdown',
      type: 'node',
      label: 'Target Breakdown by Scope?',
      inputNodeFactory: targetBreakdownInputFactory,
      ignoreBannedCharacters: true
    },
    {
      id: 'baseline_year',
      type: 'node',
      label: 'Baseline Year',
      inputNodeFactory: baselineYearInputFactory
    },
    {
      id: 'target_year',
      type: 'node',
      label: 'Target Year',
      inputNodeFactory: targetYearInputFactory
    },
    {
      id: 'target_total',
      type: 'number',
      label: `Target Total ${createdCarbonTarget.type === 'percentage' ? '(%)' : '(tCO2e)'}`,
      hideInput:
        createdCarbonTarget.breakdown === 'by_scope' ||
        !createdCarbonTarget.breakdown ||
        !createdCarbonTarget.type,
      optional: createdCarbonTarget.breakdown === 'by_scope',
      helperText:
        createdCarbonTarget.type === 'percentage'
          ? 'NOTE: Negative percentage inputs imply a reduction in carbon emissions.'
          : ''
    },
    {
      id: 'target_scope_1',
      type: 'number',
      label: `Target Scope 1 ${createdCarbonTarget.type === 'percentage' ? '(%)' : '(tCO2e)'}`,
      hideInput:
        createdCarbonTarget.breakdown === 'total' ||
        !createdCarbonTarget.breakdown ||
        !createdCarbonTarget.type,
      optional: createdCarbonTarget.breakdown === 'total',
      helperText:
        createdCarbonTarget.type === 'percentage'
          ? 'NOTE: Negative percentage inputs imply a reduction in carbon emissions.'
          : ''
    },
    {
      id: 'target_scope_2',
      type: 'number',
      label: `Target Scope 2 ${createdCarbonTarget.type === 'percentage' ? '(%)' : '(tCO2e)'}`,
      hideInput:
        createdCarbonTarget.breakdown === 'total' ||
        !createdCarbonTarget.breakdown ||
        !createdCarbonTarget.type,
      optional: createdCarbonTarget.breakdown === 'total'
    },
    {
      id: 'target_scope_3',
      type: 'number',
      label: `Target Scope 3 ${createdCarbonTarget.type === 'percentage' ? '(%)' : '(tCO2e)'}`,
      hideInput:
        createdCarbonTarget.breakdown === 'total' ||
        !createdCarbonTarget.breakdown ||
        !createdCarbonTarget.type,
      optional: createdCarbonTarget.breakdown === 'total'
    }
  ];

  const edit_inputs: EditInput[] = [
    {
      id: 'type',
      type: 'node',
      label: 'Target Type',
      inputNodeFactory: targetTypeInputFactory
    },
    {
      id: 'breakdown',
      type: 'node',
      label: 'Target Breakdown by Scope?',
      inputNodeFactory: targetBreakdownInputFactory,
      ignoreBannedCharacters: true
    },
    {
      id: 'baseline_year',
      type: 'node',
      label: 'Baseline Year',
      inputNodeFactory: baselineYearInputFactory
    },
    {
      id: 'target_year',
      type: 'node',
      label: 'Target Year',
      inputNodeFactory: targetYearInputFactory
    },
    {
      id: 'target_total',
      type: 'number',
      label: `Target Total ${actionRow?.type === 'percentage' ? '(%)' : '(tCO2e)'}`,
      defaultValue: Number(actionRow?.target_total),
      hideInput: actionRow?.breakdown === 'by_scope',
      optional: actionRow?.breakdown === 'by_scope',
      helperText:
        actionRow?.type === 'percentage'
          ? 'NOTE: Negative percentage inputs imply a reduction in carbon emissions.'
          : ''
    },
    {
      id: 'target_scope_1',
      type: 'number',
      label: `Target Scope 1 ${actionRow?.type === 'percentage' ? '(%)' : '(tCO2e)'}`,
      defaultValue: Number(actionRow?.target_scope_1),
      hideInput: actionRow?.breakdown === 'total',
      optional: actionRow?.breakdown === 'total',
      helperText:
        actionRow?.type === 'percentage'
          ? 'NOTE: Negative percentage inputs imply a reduction in carbon emissions.'
          : ''
    },
    {
      id: 'target_scope_2',
      type: 'number',
      label: `Target Scope 2 ${actionRow?.type === 'percentage' ? '(%)' : '(tCO2e)'}`,
      defaultValue: Number(actionRow?.target_scope_2),
      hideInput: actionRow?.breakdown === 'total',
      optional: actionRow?.breakdown === 'total'
    },
    {
      id: 'target_scope_3',
      type: 'number',
      label: `Target Scope 3 ${actionRow?.type === 'percentage' ? '(%)' : '(tCO2e)'}`,
      defaultValue: Number(actionRow?.target_scope_3),
      hideInput: actionRow?.breakdown === 'total',
      optional: actionRow?.breakdown === 'total'
    }
  ];

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={openDialogDelete}
        allow_delete={true}
        handleCloseDeleteModal={handleCloseDeleteModal}
        delete_label={actionRow && 'Carbon Target'}
        handleDelete={actionRow && (() => deleteRow(actionRow))}
        allow_archive={false}
      />

      {/* Side Widget Panel */}
      <Drawer
        anchor={'right'}
        open={displayWidgetPanelRight}
        onClose={() => setDisplayWidgetPanelRight(false)}
        PaperProps={{ style: { width: '30%', padding: '1.5rem' } }}
      >
        {/* Create / Edit Widget */}
        {isCreateWidget ? (
          <>
            <ConfigAddWidget
              widget_label={carbon_target_label.one}
              icon={<CreateIcon height={40} fill={primary_blue} stroke={primary_blue} />}
              inputs={create_inputs}
              handleClose={() => setDisplayWidgetPanelRight(false)}
              createFunction={async (
                type: 'percentage' | 'emissions',
                breakdown: 'total' | 'by_scope',
                baseline_year: ReportingPeriodGroup,
                target_year: ReportingPeriodGroup,
                target_total: number | undefined,
                target_scope_1: number | undefined,
                target_scope_2: number | undefined,
                target_scope_3: number | undefined
              ) => {
                await createRow(
                  type,
                  breakdown,
                  baseline_year,
                  target_year,
                  breakdown === 'total' ? target_total : undefined,
                  breakdown === 'by_scope' ? target_scope_1 : undefined,
                  breakdown === 'by_scope' ? target_scope_2 : undefined,
                  breakdown === 'by_scope' ? target_scope_3 : undefined
                );
              }}
            />
          </>
        ) : (
          <ConfigEditWidget
            widget_label={carbon_target_label.one}
            icon={<EditIcon width={40} fill={primary_blue} />}
            handleClose={() => setDisplayWidgetPanelRight(false)}
            edit_entity={actionRow}
            inputs={edit_inputs}
            handleEditInput={(input_id: string, value: unknown) =>
              actionRow && setActionRow({ ...actionRow, [input_id]: value })
            }
            confirmEditFunction={async (
              updated_carbon_target: CarbonTargetGridRow,
              original_carbon_target: CarbonTargetGridRow
            ) => {
              const validated_carbon_target: CarbonTargetGridRow = {
                ...updated_carbon_target,
                target_total:
                  updated_carbon_target.breakdown === 'total'
                    ? updated_carbon_target.target_total
                    : undefined,
                target_scope_1:
                  updated_carbon_target.breakdown === 'by_scope'
                    ? updated_carbon_target.target_scope_1
                    : undefined,
                target_scope_2:
                  updated_carbon_target.breakdown === 'by_scope'
                    ? updated_carbon_target.target_scope_2
                    : undefined,
                target_scope_3:
                  updated_carbon_target.breakdown === 'by_scope'
                    ? updated_carbon_target.target_scope_3
                    : undefined
              };
              await updateRow(validated_carbon_target, original_carbon_target);
            }}
          />
        )}
      </Drawer>
      {/* Interactive Data Table */}
      <DataGrid
        autoHeight
        rowHeight={75}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          }
        }}
        hideFooter={carbonTargetRows.length > 1 ? false : true}
        columnHeaderHeight={carbonTargetRows.length < 1 ? 0 : 56}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={gridLoading}
        rows={carbonTargetRows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => (
            <DataGridToolbar entity_label={carbon_target_label} handleCreate={handleCreateClick} />
          ),
          noRowsOverlay: () => <DataGridNoRows missing_label={carbon_target_label} />,
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        slotProps={{ toolbar: { handleCreate: handleCreateClick } }}
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
      />
    </>
  );
};

export default PanelCarbonTargets;
